import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, Shield, BarChart3, Target, Check, ChevronDown, 
  Users, Award, MessageCircle, ArrowRight, Star, Zap, 
  Lock, Download, RefreshCw, DollarSign, TrendingDown,
  AlertCircle, CheckCircle, XCircle, Clock, Eye, Filter,
  PieChart, Calendar, FileText, Smartphone, Monitor, Settings, ShoppingCart,
  Loader2, Sparkles, CreditCard, Rocket
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('antes');
  const [scrolled, setScrolled] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated checkout transition
  const handleCheckoutClick = (planType = 'monthly') => {
    setIsCheckoutLoading(true);
    setLoadingStep(0);
    
    // Animate through steps
    const steps = [1, 2, 3];
    steps.forEach((step, index) => {
      setTimeout(() => {
        setLoadingStep(step);
      }, index * 600);
    });

    // Navigate after animation completes
    setTimeout(() => {
      navigate('/checkout', { state: { planType } });
    }, 2000);
  };

  // Checkout Loading Overlay Component
  const CheckoutLoadingOverlay = () => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 backdrop-blur-xl">
      <div className="text-center max-w-md mx-auto px-6">
        {/* Animated Logo/Icon */}
        <div className="relative mb-8">
          <div className="w-24 h-24 mx-auto relative">
            {/* Outer spinning ring */}
            <div className="absolute inset-0 rounded-full border-4 border-cyan-500/30 animate-spin" style={{ animationDuration: '3s' }}></div>
            {/* Inner spinning ring */}
            <div className="absolute inset-2 rounded-full border-4 border-green-500/40 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}></div>
            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`transform transition-all duration-500 ${loadingStep >= 1 ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
                {loadingStep < 2 ? (
                  <ShoppingCart className="w-10 h-10 text-cyan-400 animate-bounce" />
                ) : loadingStep < 3 ? (
                  <CreditCard className="w-10 h-10 text-green-400 animate-pulse" />
                ) : (
                  <Rocket className="w-10 h-10 text-green-400 animate-bounce" />
                )}
              </div>
            </div>
          </div>
          
          {/* Sparkles effect */}
          <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400 animate-ping" />
          <Sparkles className="absolute -bottom-2 -left-2 w-5 h-5 text-cyan-400 animate-ping" style={{ animationDelay: '0.5s' }} />
        </div>

        {/* Loading steps */}
        <div className="space-y-4 mb-8">
          <div className={`flex items-center justify-center gap-3 transition-all duration-500 ${loadingStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${loadingStep >= 1 ? 'bg-green-500' : 'bg-slate-700'}`}>
              {loadingStep >= 1 ? <Check className="w-4 h-4 text-white" /> : <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
            </div>
            <span className="text-white font-medium">Preparando checkout seguro...</span>
          </div>
          
          <div className={`flex items-center justify-center gap-3 transition-all duration-500 ${loadingStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${loadingStep >= 2 ? 'bg-green-500' : 'bg-slate-700'}`}>
              {loadingStep >= 2 ? <Check className="w-4 h-4 text-white" /> : <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
            </div>
            <span className="text-white font-medium">Validando oferta especial...</span>
          </div>
          
          <div className={`flex items-center justify-center gap-3 transition-all duration-500 ${loadingStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${loadingStep >= 3 ? 'bg-green-500' : 'bg-slate-700'}`}>
              {loadingStep >= 3 ? <Check className="w-4 h-4 text-white" /> : <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
            </div>
            <span className="text-white font-medium">Redirecionando...</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 via-green-500 to-emerald-500 transition-all duration-500 ease-out"
            style={{ width: `${(loadingStep / 3) * 100}%` }}
          ></div>
        </div>
        
        <p className="text-gray-400 text-sm mt-4 animate-pulse">
          Aguarde um momento...
        </p>
      </div>
    </div>
  );

  const whatsappNumber = '5511916560595';
  const whatsappSupport = `https://wa.me/${whatsappNumber}?text=Olá! Preciso de suporte com o Stake Mind.`;

  const painPoints = [
    {
      icon: <XCircle className="w-6 h-6" />,
      title: "Não sabe onde está lucrando",
      description: "Impossível identificar quais casas ou tipos de apostas trazem resultado real"
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: "Perde o controle da banca",
      description: "Dinheiro entra e sai sem registro organizado, gerando confusão e prejuízo"
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: "Decisões no escuro",
      description: "Aposta sem dados concretos, baseado apenas em intuição ou 'achismos'"
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: "ROI invisível",
      description: "Não tem ideia se está ganhando ou perdendo no longo prazo"
    }
  ];

  const solutions = [
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Visão 360° do seu negócio",
      description: "Saiba EXATAMENTE onde está lucrando: por casa, por grupo, por período"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Controle total de investimentos",
      description: "Separa capital inicial, depósitos e resultados com precisão cirúrgica"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Decisões baseadas em dados",
      description: "12+ métricas profissionais para cada casa: ROI, taxa de acerto, volume, profit"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Performance em tempo real",
      description: "Gráficos, relatórios e comparações para maximizar seus lucros"
    }
  ];

  const detailedFeatures = [
    {
      category: "📊 GESTÃO DE BANCA PROFISSIONAL",
      icon: <DollarSign className="w-12 h-12" />,
      features: [
        "Controle de capital inicial e depósitos adicionais separadamente",
        "Gestão ilimitada de casas de apostas",
        "12+ métricas detalhadas por casa: ROI, profit, volume, taxa de acerto",
        "Filtros por período: mês atual, anterior, últimos 3 meses, todo período",
        "Cálculo automático de lucro/prejuízo por período",
        "Visualização profissional com cards e gráficos"
      ]
    },
    {
      category: "📈 RELATÓRIOS E ANÁLISES",
      icon: <BarChart3 className="w-12 h-12" />,
      features: [
        "Dashboard com estatísticas em tempo real",
        "Análise comparativa entre casas de apostas",
        "Identificação automática de melhor e pior casa",
        "Relatórios exportáveis em PDF e Excel",
        "Histórico completo de todas as apostas",
        "Gráficos interativos de lucro e volume"
      ]
    },
    {
      category: "🎯 SISTEMA DE APOSTAS",
      icon: <Target className="w-12 h-12" />,
      features: [
        "Registro rápido de apostas finalizadas e pendentes",
        "Sistema de grupos e categorias personalizadas",
        "Cálculo automático de profit e ROI",
        "Suporte a unidades personalizadas (stake em unidades)",
        "Registro de odds, stake e resultado",
        "Exclusão e edição de apostas"
      ]
    },
    {
      category: "💰 CONTROLE FINANCEIRO",
      icon: <PieChart className="w-12 h-12" />,
      features: [
        "Gestão de despesas operacionais",
        "Sistema de metas financeiras com prazos",
        "Acompanhamento de progresso em tempo real",
        "Cálculo de ROI final (lucro - despesas)",
        "Visão consolidada de todos os investimentos",
        "Alertas de metas próximas do prazo"
      ]
    },
    {
      category: "📱 EXPERIÊNCIA DO USUÁRIO",
      icon: <Smartphone className="w-12 h-12" />,
      features: [
        "Interface 100% responsiva (mobile e desktop)",
        "Modo claro e escuro",
        "Instalável como aplicativo (PWA)",
        "Sincronização automática",
        "Backup e recuperação de dados",
        "Navegação intuitiva e rápida"
      ]
    },
    {
      category: "🔒 SEGURANÇA E PRIVACIDADE",
      icon: <Lock className="w-12 h-12" />,
      features: [
        "Seus dados 100% privados e seguros",
        "Criptografia de ponta a ponta",
        "Backup automático diário",
        "Acesso individual por chave única",
        "Sem compartilhamento de informações",
        "Conformidade com LGPD"
      ]
    }
  ];

  const comparisonItems = [
    { feature: "Gestão de múltiplas casas", others: "❌", stakemind: "✅ Ilimitado" },
    { feature: "Análise por período customizado", others: "❌", stakemind: "✅ Completo" },
    { feature: "Separação de depósitos", others: "❌", stakemind: "✅ Automático" },
    { feature: "ROI por casa de apostas", others: "❌", stakemind: "✅ 12+ métricas" },
    { feature: "Sistema de metas", others: "❌", stakemind: "✅ Com alertas" },
    { feature: "Relatórios profissionais", others: "Básico", stakemind: "✅ Avançado" },
    { feature: "Exportação de dados", others: "❌", stakemind: "✅ PDF/Excel" },
    { feature: "Modo escuro", others: "❌", stakemind: "✅ Incluído" },
    { feature: "App mobile", others: "❌", stakemind: "✅ PWA" },
    { feature: "Suporte direto", others: "Email lento", stakemind: "✅ WhatsApp" },
    { feature: "Atualizações", others: "Raras", stakemind: "✅ Constantes" },
    { feature: "Preço", others: "R$ 49,90+", stakemind: "✅ R$ 29,90" }
  ];

  const socialProof = [
    { metric: "150+", label: "Usuários Ativos", icon: <Users /> },
    { metric: "750K+", label: "Apostas Registradas", icon: <Target /> },
    { metric: "4.9/5", label: "Avaliação Média", icon: <Star /> },
    { metric: "98%", label: "Satisfação", icon: <Award /> }
  ];

  const urgencyBenefits = [
    "🎁 Acesso imediato em até 5 minutos",
    "💎 Versão 2.8 com todas as novidades",
    "📚 Tutoriais e suporte completo",
    "🔄 Atualizações gratuitas vitalícias",
    "💰 Cancele quando quiser",
    "⚡ Setup guiado passo a passo"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden">
      
      {/* Checkout Loading Overlay */}
      {isCheckoutLoading && <CheckoutLoadingOverlay />}
      
      {/* Floating CTA Button */}
      <div className={`fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 transition-all duration-300 ${scrolled ? 'scale-100' : 'scale-0'}`}>
        <button
          onClick={() => handleCheckoutClick('monthly')}
          disabled={isCheckoutLoading}
          className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 md:px-6 py-3 md:py-4 rounded-full shadow-2xl hover:scale-110 transition-transform animate-pulse text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShoppingCart className="w-5 h-5 md:w-6 md:h-6" />
          <span className="font-bold">Assinar Agora</span>
        </button>
      </div>

      {/* Hero Section - IMPACTANTE */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20 md:py-0">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-blue-500/10 to-purple-500/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-75"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="max-w-6xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 px-4 md:px-6 py-2 md:py-3 rounded-full mb-6 md:mb-8 animate-fade-in">
              <Zap className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" />
              <span className="text-cyan-400 font-semibold text-xs md:text-base">Versão 2.8 • Sistema Mais Completo do Mercado</span>
            </div>

            {/* Logo */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_betting-wizard-4/artifacts/h7zhibl4_stakemind-logo.jpeg" 
                alt="StakeMind" 
                className="h-24 sm:h-32 md:h-40 mx-auto filter drop-shadow-2xl"
              />
            </div>

            {/* Headline REFORMULADA */}
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight px-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
                Gerencie Suas Apostas
              </span><br/>
              <span className="text-white">Como um</span> <span className="text-green-500">Profissional</span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-300 mb-4 leading-relaxed max-w-4xl mx-auto px-4">
              Descubra <strong className="text-cyan-400">exatamente onde</strong> você está lucrando ou perdendo com 
              <strong className="text-white"> dados precisos e análises completas</strong> de todas as suas apostas.
            </p>

            <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-8 max-w-3xl mx-auto px-4">
              Junte-se a mais de <strong className="text-white">150 apostadores profissionais</strong> que já 
              tomam decisões inteligentes baseadas em dados reais.
            </p>

            {/* CTA Principal */}
            <div className="flex flex-col gap-4 justify-center items-center mb-8 px-4">
              <button
                onClick={() => handleCheckoutClick('monthly')}
                disabled={isCheckoutLoading}
                className="group relative px-4 sm:px-8 md:px-10 py-4 md:py-6 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl font-bold hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center gap-2 w-full sm:w-auto max-w-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <ShoppingCart className="w-5 h-5 md:w-7 md:h-7 flex-shrink-0" />
                <span className="text-sm sm:text-base md:text-xl leading-tight">Quero Transformar Meus Resultados</span>
              </button>
            </div>

            {/* Social Proof */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 max-w-4xl mx-auto px-4">
              {socialProof.map((item, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 hover:bg-white/10 transition-all">
                  <div className="text-cyan-400 mb-1 md:mb-2 flex justify-center scale-75 md:scale-100">{item.icon}</div>
                  <div className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-0.5 md:mb-1">{item.metric}</div>
                  <div className="text-xs md:text-sm text-gray-400">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problema x Solução */}
      <section className="py-12 md:py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-center mb-4 md:mb-6 px-2">
            Você Está <span className="text-red-500">Apostando no Escuro?</span>
          </h2>
          <p className="text-base md:text-xl text-gray-400 text-center mb-8 md:mb-16 max-w-3xl mx-auto px-4">
            Se você não consegue responder essas perguntas, está deixando dinheiro na mesa:
          </p>

          <div className="grid md:grid-cols-2 gap-6 md:gap-12 max-w-6xl mx-auto mb-8 md:mb-16">
            {/* Problemas */}
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-red-500 mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                <XCircle className="w-6 h-6 md:w-8 md:h-8" />
                Sem o Stake Mind
              </h3>
              <div className="space-y-3 md:space-y-4">
                {painPoints.map((point, index) => (
                  <div key={index} className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 md:p-6">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="text-red-500 mt-1">{point.icon}</div>
                      <div>
                        <h4 className="font-bold text-white mb-1 md:mb-2 text-sm md:text-base">{point.title}</h4>
                        <p className="text-gray-400 text-xs md:text-sm">{point.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Soluções */}
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-green-500 mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                <CheckCircle className="w-6 h-6 md:w-8 md:h-8" />
                Com o Stake Mind
              </h3>
              <div className="space-y-3 md:space-y-4">
                {solutions.map((solution, index) => (
                  <div key={index} className="bg-green-500/5 border border-green-500/20 rounded-xl p-4 md:p-6">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="text-green-500 mt-1">{solution.icon}</div>
                      <div>
                        <h4 className="font-bold text-white mb-1 md:mb-2 text-sm md:text-base">{solution.title}</h4>
                        <p className="text-gray-400 text-xs md:text-sm">{solution.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center px-4">
            <button
              onClick={() => handleCheckoutClick('monthly')}
              disabled={isCheckoutLoading}
              className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 py-3 md:py-5 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl font-bold hover:scale-105 transition-transform shadow-2xl w-full sm:w-auto max-w-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
              <span className="text-sm sm:text-base md:text-xl leading-tight">Quero Ter Controle Total</span>
            </button>
          </div>
        </div>
      </section>

      {/* Recursos Detalhados */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-slate-900/50 to-slate-950">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-center mb-4 md:mb-6 px-2">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Tudo Que Você Precisa
            </span><br/>
            Em Uma Única Plataforma
          </h2>
          <p className="text-base md:text-xl text-gray-400 text-center mb-8 md:mb-16 max-w-3xl mx-auto px-4">
            Sistema completo pensado para apostadores sérios que querem resultados reais
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-7xl mx-auto">
            {detailedFeatures.map((module, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-xl md:rounded-2xl p-6 md:p-8 hover:border-cyan-500/50 transition-all">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 text-white scale-75 md:scale-100">
                  {module.icon}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-cyan-400 mb-3 md:mb-4">{module.category}</h3>
                <ul className="space-y-2 md:space-y-3">
                  {module.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 md:gap-3 text-gray-300 text-xs md:text-sm">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparação com Concorrentes */}
      <section className="py-12 md:py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-center mb-4 md:mb-6 px-2">
            Stake Mind vs <span className="text-gray-500">Outros Sistemas</span>
          </h2>
          <p className="text-base md:text-xl text-gray-400 text-center mb-8 md:mb-16 max-w-3xl mx-auto px-4">
            Veja por que apostadores profissionais escolhem o Stake Mind
          </p>

          {/* Versão Desktop - Tabela */}
          <div className="hidden md:block max-w-5xl mx-auto bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800/50">
                    <th className="px-6 py-4 text-left text-gray-400">Funcionalidade</th>
                    <th className="px-6 py-4 text-center text-gray-400">Outros Sistemas</th>
                    <th className="px-6 py-4 text-center">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 font-black">
                        STAKE MIND
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonItems.map((item, index) => (
                    <tr key={index} className="border-t border-slate-700/50 hover:bg-slate-800/30">
                      <td className="px-6 py-4 text-white font-medium">{item.feature}</td>
                      <td className="px-6 py-4 text-center text-gray-500">{item.others}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-cyan-400 font-bold">{item.stakemind}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Versão Mobile - Cards */}
          <div className="md:hidden space-y-3 max-w-xl mx-auto">
            {comparisonItems.map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-xl p-4">
                <h3 className="text-white font-bold mb-3 text-sm">{item.feature}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-400 mb-1">Outros</div>
                    <div className="text-sm text-gray-500 font-medium">{item.others}</div>
                  </div>
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-center">
                    <div className="text-xs text-cyan-400 mb-1 font-bold">STAKE MIND</div>
                    <div className="text-sm text-cyan-400 font-bold">{item.stakemind}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-12 px-4">
            <button
              onClick={() => handleCheckoutClick('monthly')}
              disabled={isCheckoutLoading}
              className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 py-3 md:py-5 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl font-bold hover:scale-105 transition-transform shadow-2xl w-full sm:w-auto max-w-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
              <span className="text-sm sm:text-base md:text-xl leading-tight">Quero o Sistema Completo</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section PREMIUM */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-center mb-4 md:mb-6 px-2">
            Investimento Que Se <span className="text-green-500">Paga Sozinho</span>
          </h2>
          <p className="text-base md:text-xl text-gray-400 text-center mb-8 md:mb-16 max-w-3xl mx-auto px-4">
            Menos que um café por dia para ter controle total e maximizar seus lucros
          </p>

          {/* Grid com 2 Planos */}
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              
              {/* PLANO MENSAL */}
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 border-2 md:border-4 border-cyan-500 rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl hover:border-cyan-400 transition-all">
                <div className="absolute -top-3 md:-top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-600 px-3 sm:px-4 md:px-6 py-1.5 md:py-2.5 rounded-full">
                  <span className="font-black text-xs sm:text-sm md:text-base whitespace-nowrap">💎 PLANO MENSAL</span>
                </div>

                <div className="text-center mb-4 sm:mb-6 mt-2 md:mt-0">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-2 sm:mb-3">
                    <span className="text-xl sm:text-2xl md:text-3xl text-gray-500 line-through">R$ 49,90</span>
                    <span className="text-4xl sm:text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                      R$ 29,90
                    </span>
                  </div>
                  <p className="text-base sm:text-lg md:text-xl text-gray-400">por mês</p>
                  <p className="text-cyan-400 font-bold mt-1 text-xs sm:text-sm md:text-base">💎 40% OFF • Cancele quando quiser</p>
                </div>

                <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 md:p-5 mb-4 sm:mb-6">
                  <h4 className="font-bold text-white mb-2 sm:mb-3 text-center text-sm sm:text-base md:text-lg">O Que Está Incluído:</h4>
                  <div className="space-y-2">
                    {urgencyBenefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2 text-gray-300 text-xs sm:text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => handleCheckoutClick('monthly')}
                  disabled={isCheckoutLoading}
                  className="block w-full py-3 sm:py-4 md:py-5 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-xl text-center font-black hover:from-cyan-600 hover:to-cyan-700 transition-all transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <div className="flex items-center justify-center gap-2">
                    <MessageCircle className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                    <span className="text-sm sm:text-base md:text-lg leading-tight">ASSINAR MENSAL</span>
                  </div>
                </button>

                <p className="text-center text-gray-400 mt-3 sm:mt-4 text-xs md:text-sm">
                  ⚡ Acesso liberado em até 5 minutos
                </p>
              </div>

              {/* PLANO TRIMESTRAL - DESTAQUE */}
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 border-2 md:border-4 border-green-500 rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl hover:border-green-400 transition-all">
                <div className="absolute -top-3 md:-top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-600 px-3 sm:px-4 md:px-6 py-1.5 md:py-2.5 rounded-full shadow-lg">
                  <span className="font-black text-xs sm:text-sm md:text-base whitespace-nowrap">🔥 PLANO TRIMESTRAL</span>
                </div>

                <div className="text-center mb-4 sm:mb-6 mt-2 md:mt-0">
                  <div className="flex flex-col items-center justify-center gap-2 mb-2 sm:mb-3">
                    <span className="text-xl sm:text-2xl md:text-3xl text-gray-500 line-through">R$ 89,70</span>
                    <span className="text-4xl sm:text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">
                      R$ 79,90
                    </span>
                  </div>
                  <p className="text-base sm:text-lg md:text-xl text-gray-400">pagamento único • 3 meses</p>
                  <p className="text-green-400 font-bold mt-1 text-xs sm:text-sm md:text-base">💰 Economize R$ 9,80 (11% OFF)</p>
                </div>

                <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 md:p-5 mb-4 sm:mb-6">
                  <h4 className="font-bold text-white mb-2 sm:mb-3 text-center text-sm sm:text-base md:text-lg">O Que Está Incluído:</h4>
                  <div className="space-y-2">
                    {urgencyBenefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2 text-gray-300 text-xs sm:text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 text-green-300 text-xs sm:text-sm font-semibold pt-2 border-t border-slate-700">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>🔥 Melhor custo-benefício</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleCheckoutClick('quarterly')}
                  disabled={isCheckoutLoading}
                  className="block w-full py-3 sm:py-4 md:py-5 bg-gradient-to-r from-green-500 to-green-600 rounded-xl text-center font-black hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <div className="flex items-center justify-center gap-2">
                    <MessageCircle className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                    <span className="text-sm sm:text-base md:text-lg leading-tight">ASSINAR TRIMESTRAL</span>
                  </div>
                </button>

                <p className="text-center text-gray-400 mt-3 sm:mt-4 text-xs md:text-sm">
                  ⚡ Acesso liberado em até 5 minutos
                </p>
              </div>

            </div>
          </div>

          {/* Garantia */}
          <div className="max-w-3xl mx-auto mt-8 md:mt-12 bg-green-500/10 border border-green-500/30 rounded-2xl p-6 md:p-8 text-center mx-4">
            <Shield className="w-12 h-12 md:w-16 md:h-16 text-green-500 mx-auto mb-3 md:mb-4" />
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">Garantia de Satisfação</h3>
            <p className="text-gray-300 text-sm md:text-base">
              Se dentro de 7 dias você não estiver satisfeito com o Stake Mind, devolvemos 100% do seu investimento. Sem perguntas, sem complicação.
            </p>
          </div>
        </div>
      </section>

      {/* Depoimentos REAIS */}
      <section className="py-12 md:py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-center mb-8 md:mb-16 px-2">
            Resultados <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Comprovados</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Carlos Mendes",
                role: "Trader Esportivo Profissional",
                text: "Em 2 meses aumentei meu ROI de 8% para 23% só por ter visibilidade REAL de onde eu estava lucrando. O investimento se pagou na primeira semana.",
                result: "+187% de ROI",
                rating: 5
              },
              {
                name: "Fernanda Costa",
                role: "Apostadora há 3 anos",
                text: "Finalmente consegui organizar minhas 5 bancas e descobri que 2 delas só me davam prejuízo. Cortei o que não funcionava e dobrei meus lucros.",
                result: "+R$ 8.340 em 3 meses",
                rating: 5
              },
              {
                name: "Rafael Santos",
                role: "Investidor Esportivo",
                text: "O sistema de relatórios é absurdo. Consigo ver tudo: casa mais lucrativa, melhor horário, melhor modalidade. São DADOS REAIS, não achismo.",
                result: "ROI de 31% mantido",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-xl md:rounded-2xl p-6 md:p-8 hover:border-cyan-500/50 transition-all">
                <div className="flex gap-1 mb-3 md:mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 md:mb-6 italic leading-relaxed text-sm md:text-base">
                  "{testimonial.text}"
                </p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 md:p-4 mb-4 md:mb-6">
                  <p className="text-green-400 font-bold text-center text-sm md:text-base">{testimonial.result}</p>
                </div>
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg md:text-xl">
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm md:text-base">{testimonial.name}</p>
                    <p className="text-xs md:text-sm text-gray-400">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-slate-900/50 to-slate-950">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-center mb-8 md:mb-16 px-2">
            Perguntas Frequentes
          </h2>

          <div className="max-w-3xl mx-auto space-y-3 md:space-y-4">
            {[
              {
                question: "Como funciona exatamente o Stake Mind?",
                answer: "O Stake Mind é uma plataforma completa de gestão financeira para apostadores. Você registra suas apostas, casas, despesas e metas. O sistema gera automaticamente mais de 12 métricas profissionais: ROI por casa, taxa de acerto, volume, lucro/prejuízo, comparações, gráficos e relatórios detalhados. Tudo em tempo real."
              },
              {
                question: "Funciona no celular?",
                answer: "Perfeitamente! O Stake Mind é 100% responsivo e pode ser instalado como aplicativo (PWA) no seu celular. Funciona offline e sincroniza quando você voltar online. Interface adaptada tanto para mobile quanto desktop."
              },
              {
                question: "Meus dados estão seguros?",
                answer: "Absolutamente! Usamos criptografia de ponta a ponta, backup automático diário, e seus dados são 100% privados. Você é o único com acesso. Conformidade total com LGPD. Pode exportar ou deletar seus dados a qualquer momento."
              },
              {
                question: "Como faço para assinar?",
                answer: "100% AUTOMÁTICO! 1) Clique em 'Assinar Agora', 2) Escolha PIX ou Cartão de Crédito, 3) Pague na hora, 4) Receba suas credenciais INSTANTANEAMENTE por WhatsApp automático. Todo o processo leva menos de 2 minutos. Acesso imediato após confirmação do pagamento!"
              },
              {
                question: "Tem limite de apostas ou casas?",
                answer: "ZERO limites! Registre apostas e casas ilimitadas. Crie quantos grupos quiser. Sistema preparado para apostadores profissionais que operam alto volume."
              },
              {
                question: "Posso cancelar quando quiser?",
                answer: "Sim! Sem fidelidade, sem burocracia. Cancele a qualquer momento através do seu painel de assinatura. Você mantém acesso até o fim do período pago. Sem pegadinhas!"
              },
              {
                question: "Tem suporte?",
                answer: "Suporte premium direto via WhatsApp para todos os assinantes. Respondemos rápido e resolvemos qualquer dúvida. Tutoriais em vídeo e documentação completa também disponíveis."
              },
              {
                question: "Vale a pena para iniciantes?",
                answer: "COM CERTEZA! Quanto antes você começar a registrar dados corretamente, mais rápido vai identificar erros e maximizar lucros. O sistema é intuitivo e tem tutoriais para iniciantes. Muitos usuários disseram 'deveria ter começado antes'."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-slate-900/50 border border-slate-700 rounded-xl overflow-hidden hover:border-cyan-500/30 transition-all">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-slate-800/30 transition-colors"
                >
                  <span className="font-bold text-white text-sm md:text-lg pr-4">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 md:w-6 md:h-6 text-cyan-400 flex-shrink-0 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-4 md:px-6 pb-4 md:pb-6">
                    <p className="text-gray-300 leading-relaxed text-sm md:text-base">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA URGENTE */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-cyan-500/10 via-blue-500/20 to-purple-500/10 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-75"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black mb-6 md:mb-8 px-2">
              Quanto Dinheiro Você Está <span className="text-red-500">Perdendo</span><br/>
              Por Não Ter Controle Total?
            </h2>
            
            <p className="text-lg md:text-2xl text-gray-300 mb-6 md:mb-8 leading-relaxed px-4">
              Cada dia sem dados é dinheiro jogado fora.<br/>
              Cada aposta sem análise é uma oportunidade perdida.<br/>
              <strong className="text-white">Cada mês sem o Stake Mind é prejuízo garantido.</strong>
            </p>

            <div className="bg-slate-900/50 backdrop-blur-sm border border-cyan-500/30 rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-12 mb-6 md:mb-8 mx-2 sm:mx-4">
              <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-8 mb-4 sm:mb-6 md:mb-8">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-5xl font-black text-cyan-400 mb-1 md:mb-2">R$29,90</div>
                  <div className="text-gray-400 text-xs md:text-base leading-tight">Investimento mensal</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-5xl font-black text-green-500 mb-1 md:mb-2">Infinito</div>
                  <div className="text-gray-400 text-xs md:text-base leading-tight">Retorno potencial</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-5xl font-black text-cyan-400 mb-1 md:mb-2">5 min</div>
                  <div className="text-gray-400 text-xs md:text-base leading-tight">Para começar</div>
                </div>
              </div>

              <button
                onClick={() => handleCheckoutClick('monthly')}
                disabled={isCheckoutLoading}
                className="flex items-center justify-center gap-2 md:gap-3 px-3 sm:px-6 md:px-12 py-3 sm:py-4 md:py-6 bg-gradient-to-r from-green-500 to-green-600 rounded-xl md:rounded-2xl font-black hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-2xl w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <ShoppingCart className="w-5 h-5 md:w-8 md:h-8 flex-shrink-0" />
                <span className="text-xs sm:text-sm md:text-2xl leading-tight text-center">QUERO MAXIMIZAR LUCROS</span>
              </button>

              <p className="text-gray-400 mt-3 sm:mt-4 md:mt-6 text-xs md:text-base text-center">
                ⚡ Acesso imediato • 💎 Sem fidelidade • 🔒 100% seguro
              </p>
            </div>

            <div className="text-gray-500 text-xs md:text-sm px-4">
              Mais de 150 apostadores já estão lucrando mais com o Stake Mind.<br/>
              Não fique para trás enquanto seus concorrentes saem na frente.
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 md:py-12 border-t border-slate-800 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
            <div className="text-center md:text-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_betting-wizard-4/artifacts/h7zhibl4_stakemind-logo.jpeg" 
                alt="StakeMind" 
                className="h-12 md:h-16 mx-auto md:mx-0"
              />
              <p className="text-gray-500 text-center md:text-left mt-2 text-sm md:text-base">
                Gestão Profissional de Apostas Esportivas
              </p>
            </div>
            
            {/* WhatsApp Suporte */}
            <div className="text-center">
              <p className="text-gray-400 mb-2 text-sm">
                💬 Suporte via WhatsApp
              </p>
              <a
                href="https://wa.me/5511916560595"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm md:text-base font-medium"
              >
                <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
                <span>(11) 91656-0595</span>
              </a>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm md:text-base">
                © 2025 StakeMind
              </p>
              <p className="text-gray-600 text-xs mt-1">
                Todos os direitos reservados
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
